import { MUIInputField, MUIInputFieldProps, StyledMUIInput } from '../MUIInputField/MUIInputField.component'

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { MUIInputFieldButton } from '../MUIInputField/MUIInputFieldButton'
import React from 'react'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'

/**
 * @interface Props Input properties
 */
export interface MUIInputFieldCounterProps extends MUIInputFieldProps {
  /** The minimum allowed value of the counter. */
  minValue?: number
  /** The maximum allowed value of the counter. */
  maxValue?: number
  /** The initial value of the counter. */
  counterValue?: number
  /** Indicates whether the input is centered or not. */
  isCenterInput?: boolean
  /** Callback function triggered on value change. */
  counterOnChange?: (value: number) => void
}

/**
 * @component MUI Input Field Counter - The extended MUIInputField with increment and decrement buttons.
 * @example
 * <MUIInputFieldCounter />
 */
export const MUIInputFieldCounter: React.FC<MUIInputFieldCounterProps> = ({
  minValue = 0,
  maxValue,
  counterValue = 0,
  isCenterInput = true,
  counterOnChange,
  ...props
}) => {

  const handleIncrement = () => {
    if (counterValue === maxValue) return // Ensure it doesn't exceed maxValue
    const newCount = counterValue + 1
    counterOnChange?.(newCount)
  }

  const handleDecrement = () => {
    if (counterValue === minValue) return // Ensure it doesn't go below minValue
    const newCount = counterValue - 1
    counterOnChange?.(newCount)
  }

  return (
    <MUIInputField
      {...props}
      customInput={
        <>
          <MUIInputFieldButton borderType="right" onClick={handleDecrement}>
            <RemoveRoundedIcon fontSize="large" />
          </MUIInputFieldButton>

          <StyledMUIInput
            value={counterValue}
            placeholder="##"
            sx={{
              input: { textAlign: isCenterInput ? 'center' : 'left' },
            }}
            inputProps={{ inputMode: 'numeric' }}
            onChange={(e) => {
              // Letters are ignored rather than read as an empty count, which would reset the counter
              if (/\D/.test(e.target.value)) return
              const value = parseInt(e.target.value)
              if (isNaN(value)) {
                counterOnChange?.(minValue)
              } else {
                counterOnChange?.(maxValue === undefined ? value : Math.min(value, maxValue))
              }
            }}
          />

          <MUIInputFieldButton onClick={handleIncrement}>
            <AddRoundedIcon fontSize="large" />
          </MUIInputFieldButton>
        </>
      }
    />
  )
}
